// Phase 4c — dashboard sub-form ops.
//
// `series` / `reference-line` / `reference-area` are positional
// children of `(cartesian …)`; `column` is a positional child of
// `(table …)`. They are NOT in the engine's `opMeta` — they register
// in `dashboardMeta` and are reachable only via their parent tile's
// child table (context restriction: a bare `(series …)` at top level
// is an `UnknownOpError`; a `(column …)` inside `(cartesian …)` is an
// `UnknownOpError` against the cartesian child table).
//
// Column slots (`:x`, `:y`, `:ref`) are explicit `(col …)` refs only
// (Phase 4c decision — no positional-dataset / bare-symbol form). They
// are evaluated lazily via `resolveColKwarg` and stored as unresolved
// `ColumnRef`s (V-shape).

import { z } from 'zod';

import { createOpTable, defineScope, type NameTable, type Scope, type SpecialOpMeta } from '../../op-meta.ts';
import { colRefArg } from './args.ts';
import { type DashboardCtx, dashboardMeta, resolveColKwarg } from './dispatch.ts';
import { ALIGNS, AXIS_SIDES, COLORS, COLUMN_FORMATS, CURVES, REF_AXES, SERIES_TYPES, STROKES } from './enums.ts';
import type {
  BandSpec,
  ColumnSpec,
  ReferenceAreaSpec,
  ReferenceLineSpec,
  SeriesSpec,
  XAxisSpec,
  YAxisSpec,
} from './value.ts';

// No sub-form takes positional args — every input is a kwarg.
const noPositional = z.tuple([]);

/**
 * `(series :type … :id … :label … :color … :axis? :curve? :x (col …) :y (col …) :stroke?)`
 * — one plotted series inside a `(cartesian …)` tile.
 */
export const series = z
  .object({
    name: z.literal('series'),
    params: noPositional,
    type: z.enum(SERIES_TYPES),
    id: z.string().min(1),
    label: z.string().min(1),
    color: z.enum(COLORS),
    axis: z.enum(AXIS_SIDES).default('left'),
    curve: z.enum(CURVES).default('smooth'),
    x: colRefArg,
    y: colRefArg,
    stroke: z.enum(STROKES).optional(),
  })
  .describe(
    'A plotted series inside `(cartesian …)`. `:x`/`:y` are explicit `(col ds colname)` refs. ' +
      '`:type` line|bar|area|dot, `:axis` left|right (default left), ' +
      '`:curve` linear|smooth|step (default smooth, line/area only).',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(series :type line :id rev :label "Revenue" :color blue :x (col w timestamp) :y (col w revenue))',
    },
    apply: async (data, ctx: DashboardCtx): Promise<SeriesSpec> => ({
      kind: 'series-spec',
      type: data.type,
      id: data.id,
      label: data.label,
      color: data.color,
      axis: data.axis,
      curve: data.curve,
      x: await resolveColKwarg(data.x, ctx, 'x'),
      y: await resolveColKwarg(data.y, ctx, 'y'),
      ...(data.stroke === undefined ? {} : { stroke: data.stroke }),
    }),
  });

/** `(reference-line :axis x|y :value N :label? :style? :color?)`. */
export const referenceLine = z
  .object({
    name: z.literal('reference-line'),
    params: noPositional,
    axis: z.enum(REF_AXES),
    value: z.number(),
    label: z.string().min(1).optional(),
    style: z.enum(STROKES).optional(),
    color: z.enum(COLORS).optional(),
  })
  .describe('A guide line inside `(cartesian …)` at a fixed `:value` on the `:axis` (x|y).')
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(reference-line :axis y :value 10000 :label "Target" :style dashed)' },
    apply: async (data): Promise<ReferenceLineSpec> => ({
      kind: 'reference-line-spec',
      axis: data.axis,
      value: data.value,
      ...(data.label === undefined ? {} : { label: data.label }),
      ...(data.style === undefined ? {} : { style: data.style }),
      ...(data.color === undefined ? {} : { color: data.color }),
    }),
  });

/** `(reference-area :axis x|y :start N :end N :label? :color?)`. */
export const referenceArea = z
  .object({
    name: z.literal('reference-area'),
    params: noPositional,
    axis: z.enum(REF_AXES),
    start: z.number(),
    end: z.number(),
    label: z.string().min(1).optional(),
    color: z.enum(COLORS).optional(),
  })
  .describe('A shaded band inside `(cartesian …)` between `:start` and `:end` on the `:axis` (x|y).')
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(reference-area :axis x :start 5 :end 10 :label "Window")' },
    apply: async (data): Promise<ReferenceAreaSpec> => ({
      kind: 'reference-area-spec',
      axis: data.axis,
      start: data.start,
      end: data.end,
      ...(data.label === undefined ? {} : { label: data.label }),
      ...(data.color === undefined ? {} : { color: data.color }),
    }),
  });
/**
 * `(band :x (col …) :lower (col …) :upper (col …) :axis? :color? :label?)`
 * — a shaded min/max band, plotted as a range area against the shared
 * `:x` domain. Independent of any `(series …)`.
 */
export const band = z
  .object({
    name: z.literal('band'),
    params: noPositional,
    x: colRefArg,
    lower: colRefArg,
    upper: colRefArg,
    axis: z.enum(AXIS_SIDES).default('left'),
    color: z.enum(COLORS).optional(),
    label: z.string().min(1).optional(),
  })
  .describe(
    'A shaded min/max band inside `(cartesian …)`. `:x`/`:lower`/`:upper` are explicit ' +
      '`(col ds colname)` refs; the band fills between `:lower` and `:upper` along `:x`. ' +
      '`:axis` left|right (default left).',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(band :x (col w ts) :lower (col w lo) :upper (col w hi) :color blue :label "p10–p90")',
    },
    apply: async (data, ctx: DashboardCtx): Promise<BandSpec> => ({
      kind: 'band-spec',
      x: await resolveColKwarg(data.x, ctx, 'x'),
      lower: await resolveColKwarg(data.lower, ctx, 'lower'),
      upper: await resolveColKwarg(data.upper, ctx, 'upper'),
      axis: data.axis,
      ...(data.color === undefined ? {} : { color: data.color }),
      ...(data.label === undefined ? {} : { label: data.label }),
    }),
  });
/** `(x-axis :label?)` — x-axis configuration inside `(cartesian …)`. */
export const xAxis = z
  .object({
    name: z.literal('x-axis'),
    params: noPositional,
    label: z.string().min(1).optional(),
  })
  .describe('Configures the `(cartesian …)` x-axis. `:label` sets the axis title.')
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(x-axis :label "Time")' },
    apply: async (data): Promise<XAxisSpec> => ({
      kind: 'x-axis-spec',
      ...(data.label === undefined ? {} : { label: data.label }),
    }),
  });

/**
 * `(y-axis :side? left|right :label?)` — y-axis configuration inside
 * `(cartesian …)`. `:side` selects which y-axis (default left).
 */
export const yAxis = z
  .object({
    name: z.literal('y-axis'),
    params: noPositional,
    side: z.enum(AXIS_SIDES).default('left'),
    label: z.string().min(1).optional(),
  })
  .describe(
    'Configures a `(cartesian …)` y-axis. `:side` left|right (default left) picks the axis; ' +
      '`:label` sets its title.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(y-axis :side left :label "Revenue (USD)")' },
    apply: async (data): Promise<YAxisSpec> => ({
      kind: 'y-axis-spec',
      side: data.side,
      ...(data.label === undefined ? {} : { label: data.label }),
    }),
  });

/** `(column :ref (col …) :label … :format … :align? :color-by-value?)`. */
export const column = z
  .object({
    name: z.literal('column'),
    params: noPositional,
    ref: colRefArg,
    label: z.string().min(1),
    format: z.enum(COLUMN_FORMATS),
    align: z.enum(ALIGNS).optional(),
    'color-by-value': z.boolean().optional(),
  })
  .describe('One column inside `(table …)`. `:ref` is an explicit `(col ds colname)`.')
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(column :ref (col wells revenue) :label "Revenue" :format currency)' },
    apply: async (data, ctx: DashboardCtx): Promise<ColumnSpec> => ({
      kind: 'column-spec',
      ref: await resolveColKwarg(data.ref, ctx, 'ref'),
      label: data.label,
      format: data.format,
      ...(data.align === undefined ? {} : { align: data.align }),
      ...(data['color-by-value'] === undefined ? {} : { colorByValue: data['color-by-value'] }),
    }),
  });

/** Sub-forms legal as positional children of `(cartesian …)`. */
export const cartesianChildScope: Scope<SpecialOpMeta> = defineScope({
  name: 'Cartesian sub-forms',
  description: 'Positional children of `(cartesian …)` — at least one `series` is required.',
  registry: dashboardMeta,
  schemas: [series, referenceLine, referenceArea, band, xAxis, yAxis],
});

/** Sub-forms legal as positional children of `(table …)`. */
export const tableChildScope: Scope<SpecialOpMeta> = defineScope({
  name: 'Table sub-forms',
  description: 'Positional children of `(table …)` — at least one `column` is required.',
  registry: dashboardMeta,
  schemas: [column],
});

/** Dispatch tables — name → schema, scoped per parent tile. */
export const CARTESIAN_CHILD_TABLE: NameTable = createOpTable([
  series,
  referenceLine,
  referenceArea,
  band,
  xAxis,
  yAxis,
]);
export const TABLE_CHILD_TABLE: NameTable = createOpTable([column]);
